<template>
    <div>
        <ModeSwitcher
            ref="modeSwitcher"
            :main-mode-text="$t('create')"
            :create-mode-text="$t('create_office')"
            :edit-mode-text="$t('edit_office')"
            @reset="editOfficeId = null"
        >
            <template v-slot:main-mode>
                <OfficeFilters
                    v-model="filters"
                    :has-active-filters="hasActiveFilters"
                    @submit="loadData()"
                    @reset="resetFilters"
                />
                <v-data-table-server
                    :headers="useHeaders"
                    :items="useItems"
                    :items-per-page="-1"
                    :items-per-page-options="[-1]"
                    :loading="isLoading"
                    :items-length="useItems.length"
                    disable-sort
                    density="compact"
                    color="primary"
                    class="elevation-1"
                    data-cy="offices-table"
                >
                    <template v-slot:item.name="{item}">
                        <div class="text-link" :data-cy="`office-link-${item.id}`" @click="editOffice(item.id)">
                            {{ item.name }}
                        </div>
                    </template>
                    <template v-slot:item.phones="{item}">
                        <div v-for="(phone, index) in item.phones" :key="index">
                            {{ phone }}
                        </div>
                    </template>
                    <template v-slot:item.active="{item}">
                        <v-switch
                            v-model="item.active"
                            color="primary"
                            :label="item.active ? $t('active') : $t('inactive')"
                            hide-details
                            :data-cy="`office-status-${item.id}`"
                            @update:model-value="changeOfficeStatus(item)"
                        />
                    </template>
                </v-data-table-server>
            </template>
            <template v-slot:additional-mode>
                <OfficeForm
                    :office-id="editOfficeId"
                    :company-id="companyId"
                    @save="save()"
                    @cancel="modeSwitcher.goToTable()"
                />
            </template>
        </ModeSwitcher>
    </div>
</template>

<script setup lang="ts">
    import OfficeFilters from '~/components/account/offices/OfficeFilters.vue'
    import type {OfficeFiltersType} from '~/utils/types/account/offices/types'
    import {clean} from '~/utils/helpers'
    import OfficeForm from '~/components/account/offices/OfficeForm.vue'

    const initOfficeFilters = (): OfficeFiltersType => ({
        name: null,
        countryId: null,
        cityId: null,
        active: true,
    })

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const props = defineProps<{
        companyId: number
    }>()

    const isLoading = ref<boolean>(false)
    const offices = ref<any[]>([])
    const filters = ref<OfficeFiltersType>(initOfficeFilters())
    const modeSwitcher = ref(null)
    const editOfficeId = ref<number>(null)

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadData()
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {
                companyId: props.companyId,
                ...filters.value,
            }
            clean(rq)

            const response = await app.$api.offices.get(rq)
            offices.value = response.offices
        } catch (e) {
            offices.value = []
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        await loadData()
        modeSwitcher.value.goToTable()
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        const headers: HeaderItemType[] = [
            {title: t('name'), value: 'name'},
            {title: t('country'), value: 'countryName'},
            {title: t('city'), value: 'cityName'},
            {title: t('type'), value: 'locationType'},
            {title: t('address'), value: 'address'},
            {title: t('phone'), value: 'phones'},
            {title: t('comments'), value: 'comments'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
        ]
        return headers
    })

    const useItems = computed(() => {
        return offices.value.map(
            ({name, countryName, cityName, locationType, street, phones, comments, active, id, companyId, zip}) => ({
                name,
                countryName,
                cityName,
                locationType: t(`locationTypes.${locationType}`),
                address: `${street || ''}${street && zip ? ', ' : ''}${zip || ''}`,
                comments,
                phones,
                active,
                id,
                companyId,
            })
        )
    })

    const resetFilters = (): void => {
        filters.value = initOfficeFilters()
        loadData()
    }

    const hasActiveFilters = computed(() => {
        return !isEqual(initOfficeFilters(), filters.value)
    })

    const editOffice = (id: number): void => {
        modeSwitcher.value.edit()
        editOfficeId.value = id
    }

    const changeOfficeStatus = async (office): Promise<void> => {
        const {id, active} = office
        try {
            isLoading.value = true
            await app.$api.offices.patch({id, active})
            await loadData()
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>
